var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useContext, useEffect } from 'react';
import VerticalCardLoader from 'syf-component-library/ui/atoms/Loader/Skeleton/VerticalCardLoader';
import { useTranslation } from 'react-i18next';
import CBRContext from '../context';
import StepButton from '../../step-button';
import useProfileQuery from '../../../services/profile/profile-query';
import useExecutionMutation from '../../../services/execution/execution-mutation';
import { CBRHeader, CardContainer } from '../eligibility/subcomponents';
import { FA_DOLLAR_SIGN, FA_HOUSE } from '../../../const/icon-props';
import ExecuteAmount from './execute-amount';
import ExecuteProfile from './execute-profile';
import Icon from '../../icon';
import Submitted from '../eligibility/submitted';
import RefundError from '../error';
import { analyticsValues, backDataProps, backToAccountManagerDataProps, submitDataProps, triggerAnalyticsKeys } from '../../../const/analytics';
import store from '../../../helpers/store';
import AddressChangeInfo from './address-change/index';
import { CBRHeaderWrapper, CancelButton, CardFooter, MainContainer, SubmitButton } from './subcomponents';
import routes from '../../../const/routes';
import { STORE_KEYS } from '../../../const/store-keys';
var Execution = function (_a) {
    var _b, _c;
    var submittedSubText = _a.submittedSubText, accountIdType = _a.accountIdType, currentBalance = _a.currentBalance, stopExecution = _a.stopExecution, authenticationRedirect = _a.authenticationRedirect;
    var t = useTranslation().t;
    var _d = useContext(CBRContext), config = _d.config, backToAccountManagerHandler = _d.backToAccountManagerHandler, triggerAnalyticsEvent = _d.triggerAnalyticsEvent;
    var accountId = store.get(STORE_KEYS.selectedIdKey, 'session');
    var _e = useProfileQuery({
        config: config,
        keyProps: {
            accountId: accountId,
            accountIdType: accountIdType
        },
        queryOptions: {
            enabled: Boolean(accountId),
            staleTime: Infinity,
            refetchOnMount: 'always'
        }
    }), profileData = _e.data, isFetching = _e.isFetching, refetch = _e.refetch, isRefetching = _e.isRefetching, isProfileError = _e.isError;
    /**
     * This useEffect is used to refetch the profile data when we get 204 status
     * which means we are already stepped up but we have to call the profile API
     * with out those extra header.
     */
    useEffect(function () {
        if (accountId && (profileData === null || profileData === void 0 ? void 0 : profileData.statusCode) === 204) {
            store.set(STORE_KEYS.isProfileEnabled, true, 'session');
            refetch();
        }
        store.set(STORE_KEYS.isProfileEnabled, false, 'session');
    }, [accountId, refetch, profileData === null || profileData === void 0 ? void 0 : profileData.statusCode]);
    var _f = useExecutionMutation({
        config: config,
        keyProps: {
            accountId: accountId,
            accountIdType: accountIdType
        }
    }), executionData = _f.data, isLoading = _f.isLoading, isError = _f.isError, isSuccess = _f.isSuccess, mutate = _f.mutate;
    var hasError = isProfileError || isError || (profileData === null || profileData === void 0 ? void 0 : profileData.statusCode) === 500;
    var isDataNotReady = isFetching || isLoading || isRefetching;
    if (isDataNotReady) {
        return (_jsx(CardContainer, __assign({ "data-testid": "loader" }, { children: _jsx(VerticalCardLoader, {}) })));
    }
    var statusCode = profileData === null || profileData === void 0 ? void 0 : profileData.statusCode;
    if (statusCode === 403) {
        var scope = 'read write write:sensitive profile:read:sensitive';
        // Note: routes.CBR should exists in vista
        var navigateTo = routes.CBR;
        store.remove('accessToken', 'session');
        authenticationRedirect({ scope: scope, navigateTo: navigateTo });
        return (_jsx(CardContainer, __assign({ "data-testid": "loader" }, { children: _jsx(VerticalCardLoader, {}) })));
    }
    var executionErrorCode = executionData === null || executionData === void 0 ? void 0 : executionData.statusCode;
    if (executionErrorCode && executionErrorCode >= 400) {
        return _jsx(RefundError, {});
    }
    if (hasError) {
        triggerAnalyticsEvent((_b = {},
            _b[triggerAnalyticsKeys.pageKind] = analyticsValues.cbr,
            _b[triggerAnalyticsKeys.pageFunction] = analyticsValues.manageCreditBalance,
            _b[triggerAnalyticsKeys.pageName] = analyticsValues.error,
            _b[triggerAnalyticsKeys.errorMessage] = analyticsValues.errorMessage,
            _b));
        // return Error when there is an error with Profile details endpoint
        return _jsx(RefundError, {});
    }
    if (!(profileData === null || profileData === void 0 ? void 0 : profileData.profile)) {
        return null;
    }
    if (!isSuccess) {
        triggerAnalyticsEvent((_c = {},
            _c[triggerAnalyticsKeys.pageKind] = analyticsValues.cbr,
            _c[triggerAnalyticsKeys.pageFunction] = analyticsValues.manageCreditBalance,
            _c[triggerAnalyticsKeys.pageName] = analyticsValues.review,
            _c));
    }
    return (_jsxs(_Fragment, { children: [_jsxs(MainContainer, { children: [!isSuccess && (_jsx(CBRHeaderWrapper, { children: _jsx(CBRHeader, { children: t('header.reviewRefundRequest') }) })), isSuccess && (_jsx(Submitted, { isCbrExecution: true, desc: t('text.requestSubmitted'), subtext: submittedSubText })), _jsx(ExecuteAmount, { icon: _jsx(Icon, { icon: FA_DOLLAR_SIGN, title: "refund check", size: "xs" }), currentBalance: currentBalance }), _jsx(ExecuteProfile, { icon: _jsx(Icon, { icon: FA_HOUSE, title: "refund check", size: "xs" }), profile: profileData.profile }), !isSuccess && _jsx(AddressChangeInfo, {})] }), _jsx(CardFooter, { children: !isSuccess ? (_jsxs(_Fragment, { children: [_jsx(CancelButton, __assign({}, backDataProps, { onClick: stopExecution }, { children: t('button.back') })), _jsx(SubmitButton, __assign({}, submitDataProps, { onClick: function () {
                                mutate({ 'cipher.accountId': accountId, accountIdType: accountIdType }, {
                                    onSuccess: function () {
                                        store.set(STORE_KEYS.isCbrExecution, false, 'session');
                                    }
                                });
                            } }, { children: t('button.submit') }))] })) : (_jsx(StepButton, __assign({ onClick: backToAccountManagerHandler, primary: true }, backToAccountManagerDataProps, { textContent: t('button.backToAccountManager') }))) })] }));
};
export default Execution;
